import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ConnectedOverlayPositionChange, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe } from '@angular/common';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { NimbusIconModule } from '@nimbus-library/features/shared/nimbus-icon.module';
import { ModalityType } from '@nimbus-global/features/shared/models/domain/modality-type.enum';
import {
  ITaskCapacityPercentage,
  ITaskCapacityPercentageModalities,
} from '@nimbus-global/features/user-tasks-capacity/models/user-tasks-capacity.model';

interface IModalityBar {
  type: ModalityType;
  labelKey: TranslationKeys;
  percentage: number;
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-task-capacity-percentage-info',
  templateUrl: './task-capacity-percentage-info.component.html',
  styleUrl: './task-capacity-percentage-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, OverlayModule, NimbusIconModule, LocalizationModule],
})
export class TaskCapacityPercentageInfoComponent implements OnChanges, OnInit {
  @Input() public taskCapacity: ITaskCapacityPercentage | null = null;
  @Input() public capabilities: ModalityType[];

  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;

  public readonly positions: ConnectedPosition[] = [
    { originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetX: 20, offsetY: 10 },
    { originX: 'center', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetX: 20, offsetY: -10 },
  ];

  public readonly isOpen$ = new BehaviorSubject<boolean>(false);
  public visibleModalities: IModalityBar[] = [];
  public cappedTotal = 0;
  public isAbove = false;

  private readonly hover$ = new Subject<boolean>();

  private readonly orderedModalities: ModalityType[] = [
    ModalityType.AudioVideo,
    ModalityType.InstantMessaging,
    ModalityType.ExternalTask,
    ModalityType.Email,
  ];

  private readonly modalityLabelKeys: Partial<Record<ModalityType, TranslationKeys>> = {
    [ModalityType.AudioVideo]: TranslationKeys.GeneralInitialModalityAudio,
    [ModalityType.InstantMessaging]: TranslationKeys.GeneralInitialModalityInstantMessaging,
    [ModalityType.ExternalTask]: TranslationKeys.GeneralInitialModalityExternalTask,
    [ModalityType.Email]: TranslationKeys.GeneralInitialModalityEmail,
  };

  private readonly modalityCapacityKeys: Partial<Record<ModalityType, keyof ITaskCapacityPercentageModalities>> = {
    [ModalityType.AudioVideo]: 'audio',
    [ModalityType.InstantMessaging]: 'instantMessaging',
    [ModalityType.ExternalTask]: 'externalTask',
    [ModalityType.Email]: 'email',
  };

  public ngOnInit(): void {
    this.hover$.pipe(debounceTime(500), untilDestroyed(this)).subscribe((value) => this.isOpen$.next(value));
  }

  public show(): void {
    this.hover$.next(true);
  }

  public hide(): void {
    this.hover$.next(false);
  }

  public onPositionChange(change: ConnectedOverlayPositionChange): void {
    // When the overlay flips to the fallback position it sits above the trigger
    // (its bottom edge connects to the origin's top), so the arrow must move to
    // the bottom of the popup to keep pointing at its origin.
    this.isAbove = change.connectionPair.overlayY === 'bottom';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.taskCapacity) {
      if (!this.taskCapacity) {
        this.isOpen$.next(false);
      }
      this.cappedTotal = Math.min(this.taskCapacity?.total ?? 0, 100);
    }

    if (changes.taskCapacity || changes.capabilities) {
      this.visibleModalities = this.orderedModalities
        .filter((type) => (this.capabilities ?? []).includes(type))
        .map((type) => ({
          type,
          labelKey: this.modalityLabelKeys[type],
          percentage: Math.min(this.taskCapacity?.[this.modalityCapacityKeys[type]!] ?? 0, 100),
        }));
    }
  }
}
